import React, {createContext, useContext,useState} from 'react';
import {View, Pressable, type PressableProps, type ViewProps, type ViewStyle} from 'react-native';
const Columns = createContext(0);
export const useButtonColumns = () => useContext(Columns);

const flatten = (nodes: React.ReactNode): React.ReactNode[] => React.Children.toArray(nodes).flatMap(child =>
  React.isValidElement<{children?: React.ReactNode}>(child) && child.type === React.Fragment ? flatten(child.props.children) : [child]);

// Native form buttons sometimes have a keyed View wrapper. Stretch both levels.
function stretch(child: React.ReactNode): React.ReactNode {
  if (!React.isValidElement(child)) return child;
  const fill: ViewStyle = {alignSelf: 'stretch', flexGrow: 1, minWidth: 0};
  if (child.type === Pressable) {
    const element = child as React.ReactElement<PressableProps>;
    const original = element.props.style;
    const center: ViewStyle = {...fill, alignItems: 'center', justifyContent: 'center'};
    return React.cloneElement(element, {style: typeof original === 'function' ? state => [original(state), center] : [original, center]});
  }
  if (child.type === View) {
    const element = child as React.ReactElement<ViewProps>;
    return React.cloneElement(element, {style: [element.props.style, fill]}, React.Children.map(element.props.children, stretch));
  }
  return child;
}

/** Deliberate, equal-width rows in both layouts, including conditional actions. */
export function ButtonRow({children, columns = 2, testID,minItemWidth}: {children: React.ReactNode; columns?: number; testID?: string;minItemWidth?:number}) {
  const [width,setWidth]=useState(0);
  const items = flatten(children), count = Math.max(1, Math.min(Math.floor(columns),minItemWidth?Math.floor((width+12)/(minItemWidth+12)):columns));
  const rows = Array.from({length: Math.ceil(items.length / count)}, (_, i) => items.slice(i * count, (i + 1) * count));
  return <View testID={testID} onLayout={minItemWidth?e=>setWidth(e.nativeEvent.layout.width):undefined} style={{alignSelf: 'stretch', gap: 12}}>{rows.map((row, i) =>
    <Columns.Provider key={i} value={row.length}><View style={{flexDirection: 'row', flexWrap: 'nowrap', alignItems: 'stretch', gap: 12}}>{row.map((child, j) =>
      <View key={React.isValidElement(child) ? child.key ?? j : j} style={{flex: 1, minWidth: 0, alignItems: 'stretch'}}>{stretch(child)}</View>
    )}</View></Columns.Provider>
  )}</View>;
}
